<template>
    <main-layout>
        <div class="page-head" slot="header">
            <div>
                <div class="page-kicker">Accounting</div>
                <h2>Receipts from the app</h2>
            </div>
        </div>

        <p class="rc-lead">Bills and receipts staff photograph in the Sterling Hub app. Each one is kept here and emailed to its
            Dext account; accounts without a Dext email-in address hold them until one is added.</p>

        <section class="list-card rc-accounts" v-loading="accountsLoading">
            <h3>Dext email-in addresses</h3>
            <p class="text-muted">In Dext, open the account and copy its "Email in" address.</p>
            <div v-for="a in accounts" :key="a.name" class="rc-account">
                <div class="rc-account-name">
                    <b>{{ a.name }}</b>
                    <span v-if="a.held" class="pill pill-warn">{{ a.held }} held</span>
                    <span v-if="a.email" class="pill pill-ok">Sends to Dext</span>
                    <span v-else class="pill pill-muted">Kept in the Portal</span>
                </div>
                <el-input v-model="emails[a.name]" size="small" placeholder="name@dext.cc" :aria-label="a.name + ' email-in address'"/>
                <el-button size="small" :disabled="(emails[a.name] || '') === (a.email || '')" @click="saveInbox(a)">Save</el-button>
            </div>
        </section>

        <div :class="{'has-panel': !!detail}">
            <div class="list-filters">
                <button v-for="c in chips" :key="c.value" type="button" class="chip" :class="{on: params.status === c.value}"
                        @click="setStatus(c.value)">{{ c.label }}{{ counts[c.value] ? ' · ' + counts[c.value] : '' }}
                </button>
                <el-select v-model="params.account" size="small" clearable placeholder="All accounts" @change="onSearch">
                    <el-option v-for="a in accounts" :key="a.name" :value="a.name" :label="a.name"/>
                </el-select>
            </div>

            <section class="list-card" v-loading="loading">
                <el-table ref="table" :data="dataList" class="list-table" empty-text="No receipts yet"
                          @selection-change="onSelectionChange" @row-click="onRowClick" :row-class-name="rowClass">
                    <el-table-column width="44" type="selection"/>
                    <el-table-column label="Photos" width="120">
                        <template slot-scope="{row}">
                            <div class="rc-thumbs">
                                <img v-for="p in row.photos.slice(0, 2)" :key="p.id" :src="p.src" alt="">
                                <span v-if="row.photos.length > 2" class="text-muted">+{{ row.photos.length - 2 }}</span>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column label="Account" min-width="200">
                        <template slot-scope="{row}">
                            <div class="cell-title">{{ row.account }}</div>
                            <div class="cell-sub">{{ row.note || '' }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column label="From" width="160">
                        <template slot-scope="{row}">{{ row.user ? (row.user.nickname || row.user.email) : '' }}</template>
                    </el-table-column>
                    <el-table-column label="Taken" width="150">
                        <template slot-scope="{row}">{{ when(row.created_at) }}</template>
                    </el-table-column>
                    <el-table-column label="Status" width="170">
                        <template slot-scope="{row}"><span class="pill" :class="'pill-' + statusOf(row).kind">{{ statusOf(row).text }}</span></template>
                    </el-table-column>
                </el-table>
                <div class="list-footer">
                    <span class="text-muted">{{ total }} receipt{{ total === 1 ? '' : 's' }}</span>
                    <el-pagination background layout="prev, pager, next" :total="total" :page-size="pageSize"
                                   :current-page="page" @current-change="onPageChange"/>
                </div>
            </section>

            <bulk-bar :count="selectionIds.length" @clear="$refs.table.clearSelection()">
                <el-button size="small" @click="forward(selectionIds)">Send to Dext</el-button>
                <el-button size="small" @click="markDone(selectionIds)">Mark handled</el-button>
            </bulk-bar>
        </div>

        <side-panel v-if="detail" open kicker="Receipt" :title="detail.account" :subtitle="detail.note || ''"
                    :record-key="detail.id" @close="detail = null">
            <div>
                <span class="pill" :class="'pill-' + statusOf(detail).kind">{{ statusOf(detail).text }}</span>
            </div>
            <div class="sp-facts">
                <div><span>From</span><b>{{ detail.user ? (detail.user.nickname || detail.user.email) : '—' }}</b></div>
                <div><span>Taken</span><b>{{ when(detail.created_at) }}</b></div>
            </div>
            <p v-if="detail.error" class="rc-error">{{ detail.error }}</p>
            <div class="sp-actions">
                <el-button v-if="detail.status !== 'sent'" type="primary" @click="forward([detail])">Send to Dext</el-button>
                <el-button v-if="detail.status !== 'sent' && detail.status !== 'done'" @click="markDone([detail])">Mark handled</el-button>
            </div>
            <section>
                <h3>Photos</h3>
                <div class="rc-photos">
                    <a v-for="p in detail.photos" :key="p.id" :href="p.src" target="_blank" rel="noopener"><img :src="p.src" alt="Receipt photo"></a>
                </div>
            </section>
        </side-panel>
    </main-layout>
</template>

<script>
import Pagination from "../mixins/Pagination";
import ApiService from "../utils/ApiService";
import SidePanel from "../components/SidePanel.vue";
import BulkBar from "../components/BulkBar.vue";
import {formatDate} from "./formHelpers";

const STATUS = {
    held: {text: 'Held — no Dext address', kind: 'warn'},
    sent: {text: 'Sent to Dext', kind: 'ok'},
    failed: {text: 'Email failed', kind: 'bad'},
    done: {text: 'Handled', kind: 'muted'},
};

export default {
    name: "Receipts",
    components: {SidePanel, BulkBar},
    mixins: [Pagination],
    data() {
        return {
            pageSize: 30,
            params: {status: '', account: ''},
            counts: {},
            chips: [
                {label: 'All', value: ''},
                {label: 'Held', value: 'held'},
                {label: 'Sent', value: 'sent'},
                {label: 'Failed', value: 'failed'},
                {label: 'Handled', value: 'done'},
            ],
            accounts: [],
            emails: {},
            accountsLoading: false,
            detail: null,
        };
    },
    methods: {
        listApi() {
            return '/sterling/receipts';
        },
        listParams() {
            return this.params;
        },
        onFinish(response) {
            this.counts = (response.data && response.data.counts) || {};
            if (this.detail) this.detail = this.dataList.find(r => r.id === this.detail.id) || null;
        },
        statusOf(r) {
            return STATUS[r.status] || {text: r.status, kind: 'muted'};
        },
        when(value) {
            if (!value) return '';
            const d = new Date(String(value).replace(' ', 'T'));
            return isNaN(d.getTime()) ? formatDate(value)
                : d.toLocaleString('en-CA', {month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'});
        },
        setStatus(value) {
            this.params.status = value;
            this.onSearch();
        },
        onRowClick(row, column) {
            if (column && column.type === 'selection') return;
            this.detail = row;
        },
        rowClass({row}) {
            return this.detail && this.detail.id === row.id ? 'is-open' : '';
        },
        loadAccounts() {
            this.accountsLoading = true;
            ApiService.get('/sterling/receipts/accounts').then(r => this.applyAccounts(r.data)).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.accountsLoading = false;
            });
        },
        applyAccounts(list) {
            this.accounts = list || [];
            const emails = {};
            this.accounts.forEach(a => {
                emails[a.name] = a.email || '';
            });
            this.emails = emails;
        },
        saveInbox(a) {
            const email = (this.emails[a.name] || '').trim();
            this.accountsLoading = true;
            ApiService.post('/sterling/receipts/accounts', {account: a.name, email}).then(r => {
                this.applyAccounts(r.data);
                const now = this.accounts.find(x => x.name === a.name) || {};
                if (email && now.held) {
                    this.$confirm(`Send the ${now.held} held receipt(s) for ${a.name} to Dext now?`, 'Address saved',
                        {confirmButtonText: 'Send now', cancelButtonText: 'Later'}).then(() => {
                        this.forwardAccount(a.name);
                    }).catch(() => {
                    });
                } else {
                    this.$message.success(email ? 'Address saved' : 'Address removed');
                }
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.accountsLoading = false;
            });
        },
        forwardAccount(name) {
            ApiService.get('/sterling/receipts', {params: {status: 'held', account: name, limit: 100}}).then(r => {
                this.forward(r.data.items || [], true);
            });
        },
        forward(list, skipConfirm) {
            const ids = list.filter(r => r.status !== 'sent').map(r => r.id);
            if (!ids.length) {
                this.$message.info('Already sent to Dext');
                return;
            }
            const go = () => {
                this.loading = true;
                ApiService.post('/sterling/receipts/forward', {ids}).then(r => {
                    const {sent, held, failed} = r.data;
                    const parts = [`${sent} sent to Dext`];
                    if (held) parts.push(`${held} still held (no address for their account)`);
                    if (failed) parts.push(`${failed} failed`);
                    this.$message[failed || held ? 'warning' : 'success'](parts.join('; '));
                    this.$refs.table && this.$refs.table.clearSelection();
                    this.fetchList();
                    this.loadAccounts();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            };
            if (skipConfirm) return go();
            this.$confirm(`Email ${ids.length} receipt(s) to Dext?`, 'Send to Dext', {confirmButtonText: 'Send'}).then(go).catch(() => {
            });
        },
        markDone(list) {
            const ids = list.map(r => r.id);
            this.$confirm(`Mark ${ids.length} receipt(s) as handled? Use this when they were entered in Dext another way.`, 'Mark handled',
                {confirmButtonText: 'Mark handled'}).then(() => {
                ApiService.post('/sterling/receipts/done', {ids}).then(() => {
                    this.$refs.table && this.$refs.table.clearSelection();
                    this.fetchList();
                    this.loadAccounts();
                }).catch(reason => {
                    this.$message.error(reason.message);
                });
            }).catch(() => {
            });
        },
    },
    mounted() {
        this.loadAccounts();
        this.fetchList();
    },
}
</script>

<style scoped>
.rc-lead {
    margin: 4px 6px 12px;
    color: #5B6478;
}

.rc-accounts {
    padding: 14px 16px;
    margin-bottom: 14px;
}

.rc-accounts h3 {
    margin: 0 0 2px;
    font-size: 15px;
    font-weight: 600;
}

.rc-account {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    padding: 8px 0;
    border-top: 1px solid #EEF0F5;
}

.rc-account-name {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
}

.rc-thumbs {
    display: flex;
    gap: 4px;
    align-items: center;
}

.rc-thumbs img {
    width: 36px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
    background: #EEF0F5;
}

.rc-photos {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.rc-photos img {
    width: 100%;
    border-radius: 8px;
    border: 1px solid #E3E6EE;
}

.rc-error {
    color: #B42318;
    font-size: 13px;
    margin: 0;
}

@media (max-width: 760px) {
    .rc-account {
        grid-template-columns: minmax(0, 1fr) auto;
    }

    .rc-account-name {
        grid-column: 1 / -1;
    }
}
</style>
